/**
 * AutosaveIndicator
 *
 * Tiny status pill rendered in the guide header that tells the user
 * their progress is being saved as they go. Four states (UX Phase
 * D-2, 2026-04-28):
 *
 *   • "Saving…"             — a patch is in flight
 *   • "Saved · 12s ago"     — last patch resolved cleanly
 *   • "Pending save"        — local edit not yet flushed to server
 *                              (between user keystroke and the 500ms
 *                              widget-debounce or 2s session-debounce
 *                              firing). Distinct from "Saving…" so
 *                              the user knows their input is on the
 *                              client only — leaving NOW would cost
 *                              the last few keystrokes.
 *   • "Offline — will retry" — last patch failed (network, server)
 *
 * Reads its inputs from props rather than the lifecycle context so
 * hosts can compose it freely (e.g. fold a form-level draft state in).
 *
 * Why this matters: cross-device resume only matters if users TRUST
 * that what they typed will be there. Multi-step widgets debounce
 * patches 500ms; the guide session debounces 2000ms. Without a visible
 * signal, users assume "submit" is the only save point — and quit
 * mid-form expecting their answers to be gone.
 */

import { useEffect, useState } from 'react';
import { useTranslation, useLocale } from '@tonnie/locale';
import { Check, AlertCircle, Clock, Loader2 } from '@tonnie/assets';
import { cn } from '@tonnie/ui';

export interface AutosaveIndicatorProps {
  /** True while any debounced PATCH or POST is in flight. */
  isSyncing: boolean;
  /**
   * True when the user has made a local edit that hasn't yet flushed
   * to the server. Drives the new "Pending save" state. Optional —
   * hosts that don't expose this signal still get the previous 3-state
   * UX.
   *
   * Hosts feed this from their dirty-form bookkeeping or the lifecycle
   * manager's `isDirty`. The state matters most during the 500ms /
   * 2000ms debounce windows: tells a fast-leaver "your last keystroke
   * isn't on the server yet."
   */
  hasUnsavedChanges?: boolean;
  /** True if the last attempt failed. Resets when isSyncing flips back true. */
  hasError?: boolean;
  /** Last successful save time. Used to render "Saved · 12s ago". */
  lastSavedAt?: Date | null;
  className?: string;
}

export function AutosaveIndicator({
  isSyncing,
  hasUnsavedChanges = false,
  hasError = false,
  lastSavedAt,
  className,
}: AutosaveIndicatorProps) {
  const { t } = useTranslation();

  // Re-render every 15s so "12s ago" counts up. Stops when no
  // lastSavedAt — saves the timer for fresh sessions.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const id = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  // "12 seconds ago" in the APP locale (F34 · F49) — the shared formatter, not a
  // browser-locale Intl.RelativeTimeFormat with an English fallback.
  const { formatRelativeTime } = useLocale();

  let label: string;
  let Icon: React.ComponentType<{ className?: string }>;
  let tone: 'syncing' | 'saved' | 'offline' | 'unsaved';

  if (isSyncing) {
    tone = 'syncing';
    Icon = Loader2;
    label = t('copilot.lifecycle.autosave.saving');
  } else if (hasError) {
    tone = 'offline';
    Icon = AlertCircle;
    label = t('copilot.lifecycle.autosave.offline');
  } else if (hasUnsavedChanges) {
    // Quiet state — visually softer than "Saving…" because no
    // network call is happening yet. Tells the user "your last
    // keystroke is on the client only; the next debounce tick will
    // flush it." Surfaces the otherwise-invisible 500ms/2000ms gap.
    tone = 'unsaved';
    Icon = Clock;
    label = t('copilot.lifecycle.autosave.unsaved');
  } else if (lastSavedAt) {
    tone = 'saved';
    Icon = Check;
    const seconds = Math.max(0, Math.floor((Date.now() - lastSavedAt.getTime()) / 1000));
    const relative =
      seconds < 5 ? t('copilot.lifecycle.autosave.justNow') : formatRelativeTime(lastSavedAt);
    label = t('copilot.lifecycle.autosave.saved', { relative });
  } else {
    // Pre-first-save → render nothing. The user hasn't done anything
    // yet; the indicator would lie.
    return null;
  }

  const toneClasses = {
    syncing: 'text-muted-foreground bg-muted/40',
    saved: 'text-tone-green bg-tone-green-subtle',
    offline: 'text-tone-amber bg-tone-amber-subtle',
    unsaved: 'text-foreground/70 bg-muted/30',
  }[tone];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-medium select-none',
        toneClasses,
        className
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <Icon className={cn('h-3.5 w-3.5', tone === 'syncing' && 'animate-spin')} aria-hidden />
      <span>{label}</span>
    </div>
  );
}
